import facebook from "@gromia/components/svg/facebook.svg";
import instagram from "@gromia/components/svg/instagram.svg";
import linkedin from "@gromia/components/svg/linkedin.svg";
import logoRed from "@gromia/components/svg/logo.svg";
import twitter from "@gromia/components/svg/twitter.svg";
import * as s from "@gromia/components/theme/t.module.css";
import { useLayoutConfig } from "@gromia/components/useLayoutConfig";
import { useFeatures, whenEnabled } from "@gromia/utils/useFeatures";
import { useMedia } from "@gromia/utils/useMedia";
import cx from "classnames";
import React from "react";
import * as spriv from "./Footer.module.css";

const FooterMenu = (props: {
  title?: NonNullable<React.ReactNode>;
  children?: NonNullable<React.ReactNode>;
}) => (
  <div
    className={cx(
      s.dPrintNone,
      s.col6,
      s.colSm4,
      s.colMd3,
      s.colLg2,
      s.mb4,
      s.mbMd0,
    )}
  >
    <h3 className={cx(s.h6, s.fontWeightBold)}>{props.title}</h3>
    <ul
      className={cx(
        s.listGroup,
        s.listGroupBorderless,
        s.listGroupFlush,
        s.mb0,
        s.pl0,
      )}
    >
      {props.children}
    </ul>
  </div>
);

const FooterMenuLink = (props: {
  href: string;
  target?: string;
  rel?: string;
  children: NonNullable<React.ReactNode>;
}) => (
  <li>
    <a
      className={cx(
        s.listGroupItem,
        s.listGroupItemAction,
        s.bgTransparent,
        s.textMuted,
        s.linkMuted,
      )}
      href={props.href}
      target={props.target}
      rel={props.rel}
    >
      {props.children}
    </a>
  </li>
);

export const Footer = (props: { disableLinks?: boolean }) => {
  const config = useLayoutConfig();
  const features = useFeatures();

  return (
    <footer className={cx(s.spaceTop1, s.bgWhite)}>
      <div
        className={cx(
          s.container,
          s.borderBottom,
          props.disableLinks ? s.dNone : null,
        )}
      >
        <div className={cx(s.row, s.mb5)}>
          <div className={cx(s.colLg3, s.mb5, s.mbLg0)}>
            <div
              className={cx(s.dFlex, s.alignSelfStart, s.flexColumn, s.h100)}
            >
              <a
                className={cx(s.dFlex, s.alignItemsCenter, s.mb3)}
                href="/"
                aria-label="Gromia"
              >
                <img alt="Gromia" src={logoRed} />
              </a>
            </div>
          </div>

          {useMedia().md
            ? (
              <div
                className={cx(
                  s.col6,
                  s.colSm4,
                  s.colMd3,
                  s.colLg2,
                  s.mb4,
                  s.mbMd0,
                )}
              />
            )
            : null}

          <FooterMenu title="Risorse">
            <FooterMenuLink href="https://www.gromia.com/blog">
              Blog
            </FooterMenuLink>
            <FooterMenuLink href="/careers">Careers</FooterMenuLink>
            <FooterMenuLink href="/agenti/">
              Agenti
            </FooterMenuLink>
            <FooterMenuLink href="/diventa-agente/">
              Diventa agente
            </FooterMenuLink>
            <FooterMenuLink href="/dashboard/">Area riservata</FooterMenuLink>
          </FooterMenu>
          <FooterMenu title="Legal">
            <FooterMenuLink
              href="https://www.iubenda.com/termini-e-condizioni/79909994"
              rel="noopener"
              target="_blank"
            >
              Condizioni di utilizzo
            </FooterMenuLink>
            <FooterMenuLink
              href="https://www.iubenda.com/privacy-policy/79909994"
              rel="noopener"
              target="_blank"
            >
              Privacy
            </FooterMenuLink>
            <FooterMenuLink
              href="https://www.iubenda.com/privacy-policy/79909994/cookie-policy"
              rel="noopener"
              target="_blank"
            >
              Cookie
            </FooterMenuLink>
          </FooterMenu>

          <div className={cx(s.dPrintNone, s.colMd3, s.colLg2)}>
            <h3 className={cx(s.h6, s.fontWeightBold)}>Contact</h3>

            <address>
              <ul
                className={cx(
                  s.listGroup,
                  s.listGroupFlush,
                  s.listGroupBorderless,
                  s.mb0,
                  s.pl0,
                  s.textMuted,
                )}
              >
                {whenEnabled(
                  features.global.showPhone,
                  () => (
                    <li className={cx(s.listGroupItem)}>
                      {config.phoneLandlineLabel}
                    </li>
                  ),
                )}
                <li className={cx(s.listGroupItem)}>
                  <a href={`mailto:${config.supportEmail}`}>
                    {config.supportEmail}
                  </a>
                </li>
                <li className={cx(s.listGroupItem)}>
                  Viale Cassala, 57
                  <br />
                  20143 Milano – Italia
                </li>
                <li className={cx(s.listGroupItem)}>
                  Piazza del Popolo, 18
                  <br />
                  00187 Roma – Italia
                </li>
              </ul>
            </address>
          </div>
        </div>
      </div>
      <div
        className={cx(
          s.container,
          s.dFlex,
          s.justifyContentBetween,
          s.alignItemsCenter,
          s.pt3,
          s.pb3,
        )}
      >
        <p className={cx(s.small, s.textMuted, s.mb0)}>
          © {new Date().getFullYear()} Gromia – P.IVA 08726630968
        </p>
        <ul
          className={cx(
            s.dPrintNone,
            s.listInline,
            s.mb0,
            props.disableLinks ? s.dNone : null,
          )}
        >
          <SocialIcon
            alt="Linkedin"
            href="https://www.linkedin.com/company/gromia/"
            src={linkedin}
          />
          <SocialIcon
            alt="Facebook"
            href="https://www.facebook.com/gromiaapp/"
            src={facebook}
          />
          <SocialIcon
            alt="Twitter"
            href="https://twitter.com/gromiaapp/"
            src={twitter}
          />
          <SocialIcon
            alt="Instagram"
            href="https://www.instagram.com/gromia_ita/"
            src={instagram}
          />
        </ul>
      </div>
    </footer>
  );
};

type SocialIconProps = {
  href: string;
  src: string;
  alt: string;
};

function SocialIcon(props: SocialIconProps) {
  return (
    <li className={cx(s.listInlineItem)}>
      <a
        className={cx(spriv.socialIcon)}
        href={props.href}
        target="_blank"
        rel="noopener"
      >
        <img alt={props.alt} src={props.src} />
      </a>
    </li>
  );
}
